import { Box } from '@mui/material';
import { useMemo, useCallback, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from 'src/components/new/Typography';
import { AppDispatch, RootState } from 'src/store/store';
import { updateUnsavedPostageRates } from 'src/features/admin/adminSlice';
import PricingTable from 'src/components/table/PricingTable';
import PostageRateInput from '../pricing-inputs/PostageRateInput';
import { mapPostageType } from './FirstClassPostageRates';
import { formatPrice, validateInput } from '../utils/utils';

const RATE_FIELDS = [
  'priority_express_domestic',
  'priority_express_canada',
  'priority_express_mexico',
  'priority_express_international',
] as const;

const WEIGHT_MAP: Record<string, { weight: string; range: ReactNode }> = {
  '1': {
    weight: '1 - 21',
    range: (
      <>
        1 -&gt; <b>120</b>
      </>
    ),
  },
  '22': {
    weight: '21 - 24',
    range: (
      <>
        121 -&gt; <b>140</b>
      </>
    ),
  },
  '92': {
    weight: '92',
    range: (
      <>
        141 -&gt; <b>500</b>
      </>
    ),
  },
};

interface RateCell {
  id: number;
  price: number;
}

interface Row {
  weight: string;
  packageType: string;
  sheetRange: React.ReactNode;
  [key: string]: any;
}

const columns = [
  {
    headerName: (
      <>
        <p style={{ margin: 0 }}>Weight</p>
        <p style={{ margin: 0, color: '#7e828b' }}>Not Over (oz)</p>
      </>
    ),
    field: 'weight',
    width: 70,
  },
  { headerName: 'Sheet Range', field: 'sheetRange', width: 50 },
  {
    headerName: 'Package',
    field: 'packageType',
    width: 120,
    customHeaderStyle: { borderRight: '1px solid rgba(224,224,224,1)' },
    customCellStyle: { borderRight: '1px solid rgba(224,224,224,1)' },
  },
  ...RATE_FIELDS.map((field) => ({
    headerName:
      field.replace('priority_express_', '').replace('_', ' ').charAt(0).toUpperCase() +
      field.replace('priority_express_', '').replace('_', ' ').slice(1),
    field,
    width: 120,
    customHeaderStyle: { textAlign: 'center' as const },
  })),
];

interface Props {
  isEditingEnabled: boolean;
}

const PriorityPostageRates: React.FC<Props> = ({ isEditingEnabled }) => {
  const dispatch = useDispatch<AppDispatch>();

  const postageRates = useSelector((s: RootState) => s.admin.sortedPostageRatesV2);
  const unsavedPostageRates = useSelector((s: RootState) => s.admin.unsavedPostageRates);

  const changedIds = useMemo(() => new Set(Object.keys(unsavedPostageRates).map(Number)), [unsavedPostageRates]);

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, id: number, packageType: string, mailingType: string) => {
      const isValidInput = validateInput(e.target.value, 2);
      if (!isValidInput) return;
      dispatch(
        updateUnsavedPostageRates({
          id,
          value: formatPrice(e.target.value, 2),
          packageType,
          mailingType,
        }),
      );
    },
    [dispatch],
  );

  const data: Row[] = useMemo(() => {
    return Object.entries(postageRates.priority_express ?? {})
      .filter(([weight]) => WEIGHT_MAP[weight])
      .map(([weight, rates]: any) => {
        const base = WEIGHT_MAP[weight];

        const row: Row = {
          weight: base.weight,
          packageType: rates[0].packageType,
          sheetRange: base.range,
          hasBottomBorder: true,
        };

        rates.forEach((r: any) => {
          row[r.mailingType] = { id: r.id, price: r.price };
        });

        return row;
      });
  }, [postageRates]);

  const renderRateInput = (row: Row, field: string) => {
    const cell: RateCell = row[field];

    return (
      <PostageRateInput
        isEditingEnabled={isEditingEnabled}
        onInputChange={onInputChange}
        isDirty={changedIds.has(cell.id)}
        packageType={row.packageType}
        mailingType={field}
        id={cell.id}
        value={unsavedPostageRates[cell.id] ?? `$${(cell?.price ?? 0).toFixed(2)}`}
      />
    );
  };

  const renderCell = (row: Row, column: any) => {
    if (RATE_FIELDS.includes(column.field)) {
      return renderRateInput(row, column.field);
    }

    if (column.field === 'packageType') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ p: 1, borderRadius: '999px', backgroundColor: '#f0f6ff' }}>
            <Typography fontWeight={500} fontSize={14}>
              {mapPostageType[row.packageType as keyof typeof mapPostageType]}
            </Typography>
          </Box>
        </Box>
      );
    }

    return row[column.field];
  };

  return <PricingTable mainHeaderName="Priority Express" data={data} columns={columns} renderCell={renderCell} />;
};

export default PriorityPostageRates;
